import { useEffect, useState } from "react";
import ButtonIconH from "./ButtonIconH";
import DataTable from "react-data-table-component";
import InputData from "./InputData";
import InputTextArea from "./InputTextArea";
import { extraColumns } from "../js/tableColumns";
import "../css/Extras.css";
import { userData, focusElement, selectedOptic } from "../js/utils";
import { post } from "../js/post";
import InputIconSearch from "./InputIconSearch";
import TooltipBox from "./Tooltip";
import { calculateDtos } from "../js/calculateDtos";
import { getPatientProductPrice } from "../js/patientProductPrice";

export default function Extras({
  id,
  patientId,
  createPopupQuestion,
  removePopupQuestion,
}) {
  const [currentExtras, setCurrentExtras] = useState([]);
  const [currentExtra, setCurrentExtra] = useState({
    codigo: "",
    descripcion: "",
    pvp: "",
    iva: "",
    dto: 0,
    dtoMaximo: "",
    total: "",
  });
  const [newTotal, setNewTotal] = useState(0);

  const [tiposIVA, setTiposIVA] = useState(null);
  const [IVASuperRed, setIVASuperRed] = useState(4);
  const [prevIVA, setPrevIVA] = useState("");

  const resetExtra = () => {
    setCurrentExtra({
      codigo: "",
      descripcion: "",
      pvp: "",
      iva: "",
      dto: 0,
      dtoMaximo: "",
      total: "",
    });
    setNewTotal(0);
  };

  const roundDto = (dto) => Math.round(parseFloat(dto) || 0);

  const handleValue = (field, value) => {
    setCurrentExtra((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const columnsWithInputs = extraColumns.map((col) => {
    if (col.name === "") {
      return {
        ...col,
        cell: (row) => (
          <>
            <img
              src={`/images/remove-blue.svg`}
              className="icon-pref"
              alt="Eliminar producto"
              data-tooltip-id={row.id}
              onClick={() => {
                createPopupQuestion(
                  `¿Desea eliminar el extra?`,
                  async () => {
                    await deleteExtra(row.id);
                    await getExtras();
                    removePopupQuestion();
                  },
                  removePopupQuestion,
                  "Sí",
                  "No",
                );
              }}
            ></img>
            <TooltipBox
              id={String(row.id)}
              content={
                <div className="opt-table-tooltip grid-5">
                  <div className="cell combined-cells-5 cell-start-content">
                    Borrar
                  </div>
                </div>
              }
            ></TooltipBox>
          </>
        ),
      };
    }

    return col;
  });

  async function addExtra() {
    if (
      currentExtra.descripcion !== "" &&
      currentExtra.pvp !== "" &&
      currentExtra.iva !== "" &&
      currentExtra.dto !== "" &&
      currentExtra.total !== ""
    ) {
      const response = await post("/support-extra/fichas/CrearFichaExtra.php", {
        token: userData.token,
        idFicha: id,
        ...currentExtra,
      });

      if (response.idExtra) {
        getExtras();
        resetExtra();
      }
    } else {
      createPopupQuestion(
        `Por favor, rellene los campos obligatorios.`,
        async () => {
          removePopupQuestion();
        },
        null,
        "Entendido",
      );
    }
  }

  const deleteExtra = async (id) => {
    await post("/support-extra/fichas/EliminarFichaExtra.php", {
      token: userData.token,
      idExtra: id,
    });
  };

  const getExtras = async () => {
    const response = await post("/support-extra/fichas/ListarFichaExtra.php", {
      token: userData.token,
      idFicha: id,
    });
    setCurrentExtras(response);
  };

  function calculateTotal(pvp, discountPercent, ivaPercent) {
    const dto = parseFloat(discountPercent);
    const iva = parseFloat(ivaPercent);

    const discount = pvp * ((isNaN(dto) ? 0 : dto) / 100);
    const total = pvp - discount;

    return total.toFixed(2);
  }

  function recalculateTotal() {
    const line = [
      {
        total: currentExtra.total,
        dto: currentExtra.dto,
        id: currentExtra.codigo,
        dtoMax: currentExtra.dtoMaximo,
      },
    ];
    const response = calculateDtos(line, newTotal, false);

    if (response.error === "") {
      setCurrentExtra((prev) => {
        return {
          ...prev,
          dto: response.lines[0].dto,
          total: response.lines[0].total,
        };
      });
    } else {
      createPopupQuestion(
        response.error,
        async () => {
          removePopupQuestion();
          const total = calculateTotal(currentExtra.pvp, 0, currentExtra.iva);
          setCurrentExtra((prev) => {
            return {
              ...prev,
              total: total,
              dto: 0,
            };
          });
          setNewTotal(total);
          focusElement(603);
        },
        null,
        "Entendido",
      );
    }
  }

  const searchProductByCode = async () => {
    const response = await post("ObtenerProducto.php", {
      token: userData.token,
      idOptica: selectedOptic.id,
      codigo: currentExtra.codigo,
    });

    if (response.codigoOPTIWIN) {
      const price = await getPatientProductPrice({
        product: response,
        idPaciente: patientId,
        createPopupQuestion,
        removePopupQuestion,
      });
      const total = calculateTotal(price.pvp, price.descuento, response.iva);
      setCurrentExtra((prev) => ({
        ...prev,
        codigo: response.codigoOPTIWIN,
        descripcion: response.nombre,
        pvp: price.pvp,
        iva: response.iva,
        dto: price.descuento,
        dtoMaximo: response.dtoMaximo,
        total,
      }));
      setNewTotal(total);
      focusElement(601);
    }
  };

  const loadTiposIVA = async () => {
    const response = await post("ListarTiposIVA.php", {
      token: userData.token,
      idOptica: selectedOptic.id,
    });
    setTiposIVA(response.tiposIVA);
    setIVASuperRed(response.superReducido);
  };

  useEffect(() => {
    focusElement(600);
    loadTiposIVA();
    getExtras();
  }, []);

  return (
    <div className="extra-container">
      <div className="extra-container-info extra-table">
        <div className="container-panel" style={{ flex: 1 }}>
          <DataTable
            columns={columnsWithInputs}
            data={currentExtras}
            noDataComponent="No hay datos disponibles."
            className="custom-datatable non-interactive"
          />
        </div>
        <div className="new-extra-content container-panel">
          <div className="opt-table-content grid-5">
            <div className="cell combined-cells-5 opt-table-subtitle">
              Nuevo extra
            </div>
            <div className="cell cell-label cell-start-content">Código</div>
            <div className="cell combined-cells-4">
              <InputIconSearch
                tabIndex={600}
                type="type-alphanum-ext"
                size={6}
                maxLength={10}
                input={currentExtra.codigo}
                onChange={(e) => {
                  handleValue("codigo", e);
                }}
                onClear={() => {
                  handleValue("codigo", "");
                }}
                onEnter={searchProductByCode}
              ></InputIconSearch>
            </div>
            <div className="cell cell-label cell-start-content">PVP (*)</div>
            <div className="cell">
              <InputData
                tabIndex={601}
                type="type-positive-float"
                size={6}
                input={currentExtra.pvp}
                onChange={(e) => {
                  handleValue("pvp", e);
                }}
                onBlur={(e) => {
                  const total = calculateTotal(
                    e,
                    currentExtra.dto,
                    currentExtra.iva,
                  );
                  setCurrentExtra({
                    ...currentExtra,
                    total: total,
                  });
                  setNewTotal(total);
                }}
              ></InputData>
            </div>
            <div className="cell"></div>
            <div className="cell cell-label cell-start-content">IVA % (*)</div>
            <div className="cell">
              <InputData
                tabIndex={602}
                type="type-positive-integer"
                size={6}
                input={currentExtra.iva}
                onChange={(e) => {
                  handleValue("iva", e);
                }}
                onBlur={(e) => {
                  const iva = parseInt(e);
                  if (tiposIVA.includes(iva)) {
                    if (iva === IVASuperRed) {
                      currentExtra.pvp =
                        (parseFloat(currentExtra.pvp) * iva) / 100 +
                        parseFloat(currentExtra.pvp);
                    }
                    const total = calculateTotal(
                      currentExtra.pvp,
                      currentExtra.dto,
                      e,
                    );
                    setCurrentExtra({
                      ...currentExtra,
                      total: total,
                    });
                    setNewTotal(total);
                    setPrevIVA(iva);
                  } else {
                    createPopupQuestion(
                      "Tipo de IVA inválido: " + iva + "%.",
                      () => {
                        const currentExtraN = structuredClone(currentExtra);
                        currentExtraN.iva = prevIVA;
                        setCurrentExtra(currentExtraN);
                        removePopupQuestion();
                      },
                      null,
                      "Cerrar",
                      "",
                    );
                  }
                }}
              ></InputData>
            </div>
            <div className="cell cell-label cell-start-content">Dto % (*)</div>
            <div className="cell">
              <InputData
                tabIndex={603}
                type="type-positive-integer"
                size={6}
                input={roundDto(currentExtra.dto)}
                onChange={() => {}}
                onBlur={(e) => {
                  // El dto se guarda como float, pero el input muestra el
                  // entero: solo se recalcula si cambia el valor visible.
                  const inputValue = Number(e) || 0;
                  if (inputValue === roundDto(currentExtra.dto)) return;
                  const total = calculateTotal(
                    currentExtra.pvp,
                    inputValue,
                    currentExtra.iva,
                  );
                  setCurrentExtra({
                    ...currentExtra,
                    dto: inputValue,
                    total: total,
                  });
                  setNewTotal(total);
                }}
              ></InputData>
            </div>
            <div className="cell"></div>
            <div className="cell cell-label cell-start-content">Total (*)</div>
            <div className="cell">
              <InputData
                tabIndex={604}
                type="type-positive-float"
                size={6}
                input={newTotal}
                onChange={(e) => {
                  setNewTotal(e);
                }}
                onBlur={recalculateTotal}
              ></InputData>
            </div>
            <div className="cell cell-label cell-start-content combined-cells-5">
              Descripción (*)
            </div>
            <div className="cell combined-cells-5">
              <InputTextArea
                maxLength={60}
                tabIndex={605}
                type="type-full"
                size={0}
                input={currentExtra.descripcion}
                onChange={(e) => {
                  handleValue("descripcion", e);
                }}
              ></InputTextArea>
            </div>
          </div>
          <div className="popup-buttons">
            <ButtonIconH text="Añadir" onClick={addExtra}></ButtonIconH>
            <ButtonIconH text="Limpiar" onClick={resetExtra}></ButtonIconH>
          </div>
        </div>
      </div>
    </div>
  );
}
